import React, { useEffect, useRef, useState } from 'react';

/* ─── DARK TRANSYLVANIA (footer scene) ──────────
   Layered Carpathian ridges in fog, a Bran-style castle on a hill with a
   holographic outline and a scanning light, candle-lit windows, a full moon,
   pine forest and bats. SVG + CSS only; "slice" keeps proportions on wide
   screens (it crops instead of stretching). Animations pause off-screen and
   stop with prefers-reduced-motion. */

const W = 1440;
const H = 360;
const GROUND = '#0a0b0f'; // matches the footer below

// Deterministic pseudo-random, so the forest doesn't reshuffle on re-render
const rand = seed => {
  const x = Math.sin(seed * 999.13) * 43758.5453;
  return x - Math.floor(x);
};

const ridge = (base, amp, step, seed) => {
  let d = `M0 ${H} L0 ${base}`;
  for (let x = 0; x <= W; x += step) {
    const y = base - amp * (0.35 + 0.65 * rand(seed + x)) * (0.6 + 0.4 * Math.sin(x / 170 + seed));
    d += ` L${x} ${y.toFixed(1)}`;
  }
  return `${d} L${W} ${H} Z`;
};

// Hill the castle stands on
const HILL = `M0 ${H} L0 318 C 260 312, 470 292, 590 262 C 650 246, 690 236, 720 236
  C 750 236, 790 246, 850 262 C 970 292, 1180 312, ${W} 318 L${W} ${H} Z`;

// The hill's top edge as sampled points (same curves as HILL), to plant trees on it
const HILL_CURVES = [
  [[0, 318], [260, 312], [470, 292], [590, 262]],
  [[590, 262], [650, 246], [690, 236], [720, 236]],
  [[720, 236], [750, 236], [790, 246], [850, 262]],
  [[850, 262], [970, 292], [1180, 312], [W, 318]],
];
const HILL_POINTS = HILL_CURVES.flatMap(([a, b, c, d]) => Array.from({ length: 41 }, (_, i) => {
  const t = i / 40; const u = 1 - t;
  return [
    u * u * u * a[0] + 3 * u * u * t * b[0] + 3 * u * t * t * c[0] + t * t * t * d[0],
    u * u * u * a[1] + 3 * u * u * t * b[1] + 3 * u * t * t * c[1] + t * t * t * d[1],
  ];
}));
const hillY = x => {
  const i = HILL_POINTS.findIndex(p => p[0] >= x);
  if (i <= 0) return HILL_POINTS[Math.max(i, 0)][1];
  const [x0, y0] = HILL_POINTS[i - 1];
  const [x1, y1] = HILL_POINTS[i];
  return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0 || 1);
};

const TREES = (() => {
  const trees = [];
  for (let x = 6; x < W; x += 15 + rand(x) * 16) {
    if (x > 560 && x < 880) continue; // leave the castle clear
    const h = 16 + rand(x + 7) * 26;
    const w = h * 0.42;
    const y = hillY(x) + 4;
    trees.push(`M${x.toFixed(1)} ${(y - h).toFixed(1)} L${(x - w).toFixed(1)} ${y.toFixed(1)} L${(x + w).toFixed(1)} ${y.toFixed(1)} Z`);
  }
  return trees.join(' ');
})();

const STARS = Array.from({ length: 34 }, (_, i) => ({
  x: rand(i + 1) * W,
  y: 80 + rand(i + 50) * 120,
  r: 0.6 + rand(i + 90) * 1.2,
  d: (rand(i + 130) * 4).toFixed(2),
}));

// Castle parts: [type, ...geometry]
const CASTLE = [
  // curtain wall + battlements
  ['rect', 636, 196, 168, 40],
  ...Array.from({ length: 8 }, (_, i) => ['rect', 640 + i * 21, 188, 11, 9]),
  // far-left turret
  ['rect', 618, 182, 20, 52], ['poly', '612,183 628,150 644,183'],
  // left tower
  ['rect', 650, 160, 28, 76], ['poly', '643,162 664,112 685,162'],
  // keep
  ['rect', 692, 138, 58, 98], ['poly', '684,140 721,86 758,140'],
  // right tower (tallest roof)
  ['rect', 762, 150, 30, 86], ['poly', '755,152 777,96 799,152'],
  // gate tower
  ['rect', 708, 206, 26, 30],
];

const WINDOWS = [
  [700, 160, 7, 11], [722, 160, 7, 11], [738, 160, 7, 11], [712, 184, 7, 10], [730, 184, 7, 10],
  [659, 180, 6, 10], [669, 200, 5, 8], [772, 172, 6, 10], [781, 196, 6, 10], [624, 200, 5, 8],
  [646, 212, 5, 7], [794, 212, 5, 7],
];

const BATS = [
  { top: '14%', dur: 22, delay: -3, size: 34 },
  { top: '26%', dur: 30, delay: -14, size: 24 },
  { top: '8%', dur: 36, delay: -25, size: 18 },
];

const css = `
.tv-scene { position: relative; height: clamp(230px, 26vw, 540px); overflow: hidden; }
.tv-scene svg { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
.tv-scene.paused *, .tv-scene.paused { animation-play-state: paused !important; }
.tv-star { animation: tvTwinkle 3.2s ease-in-out infinite; }
.tv-window { animation: tvFlicker 2.6s steps(1) infinite; }
.tv-fog { position: absolute; left: -50%; width: 200%; pointer-events: none;
  background: radial-gradient(ellipse 18% 55% at 25% 50%, rgba(189,147,249,.14), transparent 70%),
              radial-gradient(ellipse 22% 60% at 70% 50%, rgba(139,233,253,.10), transparent 70%),
              radial-gradient(ellipse 15% 50% at 95% 50%, rgba(255,121,198,.10), transparent 70%);
  background-size: 50% 100%; background-repeat: repeat-x; }
.tv-fog.a { bottom: 16%; height: 30%; animation: tvDrift 60s linear infinite; }
.tv-fog.b { bottom: 4%; height: 26%; opacity: .8; animation: tvDrift 42s linear infinite reverse; }
.tv-bat { position: absolute; left: 0; animation: tvFly linear infinite; }
.tv-bat svg { position: static; width: 100%; height: 100%; animation: tvFlap .45s ease-in-out infinite alternate;
  filter: drop-shadow(0 0 6px rgba(189,147,249,.6)); }
@keyframes tvTwinkle { 0%,100% { opacity: .25 } 50% { opacity: .9 } }
@keyframes tvFlicker { 0%,100% { opacity: .95 } 12% { opacity: .6 } 14% { opacity: 1 } 48% { opacity: .8 } 70% { opacity: .5 } 72% { opacity: .95 } }
@keyframes tvDrift { from { transform: translateX(0) } to { transform: translateX(25%) } }
@keyframes tvFly { 0% { transform: translate(-60px, 0) } 25% { transform: translate(25vw, -14px) }
  50% { transform: translate(50vw, 8px) } 75% { transform: translate(75vw, -10px) } 100% { transform: translate(calc(100vw + 60px), 0) } }
@keyframes tvFlap { from { transform: scaleY(1) } to { transform: scaleY(.45) } }
@media (prefers-reduced-motion: reduce) { .tv-scene *, .tv-scene { animation: none !important; } }
`;

const TransylvaniaScene = () => {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setPaused(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`tv-scene${paused ? ' paused' : ''}`} aria-hidden="true">
      <style>{css}</style>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax slice">
        <defs>
          <radialGradient id="tvMoonHalo">
            <stop offset="0%" stopColor="#f1fa8c" stopOpacity=".35" />
            <stop offset="45%" stopColor="#bd93f9" stopOpacity=".12" />
            <stop offset="100%" stopColor="#bd93f9" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="tvFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={GROUND} stopOpacity="0" />
            <stop offset="100%" stopColor={GROUND} stopOpacity="1" />
          </linearGradient>
          {/* Holographic outline with a light band sweeping across the castle */}
          <linearGradient id="tvHolo" gradientUnits="userSpaceOnUse" x1="560" y1="0" x2="880" y2="0">
            <stop offset="0%" stopColor="#bd93f9" />
            <stop offset="42%" stopColor="#8be9fd" />
            <stop offset="50%" stopColor="#ffffff" />
            <stop offset="58%" stopColor="#8be9fd" />
            <stop offset="100%" stopColor="#ff79c6" />
            <animateTransform attributeName="gradientTransform" type="translate" values="-320 0; 320 0; -320 0" dur="9s" repeatCount="indefinite" />
          </linearGradient>
          <filter id="tvGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.2" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="tvWindowGlow" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {STARS.map((s, i) => (
          <circle key={i} className="tv-star" cx={s.x} cy={s.y} r={s.r} fill="#f8f8f2" style={{ animationDelay: `${s.d}s` }} />
        ))}

        {/* Moon */}
        <circle cx="1150" cy="150" r="95" fill="url(#tvMoonHalo)" opacity=".6">
          {/* SMIL, not CSS: a CSS-animated SVG element got its own layer and showed a dark box */}
          <animate attributeName="r" values="92;102;92" dur="7s" repeatCount="indefinite" />
          <animate attributeName="opacity" values=".55;.8;.55" dur="7s" repeatCount="indefinite" />
        </circle>
        <circle cx="1150" cy="150" r="30" fill="#efeac6" opacity=".92" />
        <circle cx="1140" cy="142" r="6" fill="#d9d2a6" opacity=".6" />
        <circle cx="1161" cy="160" r="4" fill="#d9d2a6" opacity=".5" />

        {/* Carpathian ridges, far to near */}
        <path d={ridge(215, 95, 36, 3)} fill="#1b1230" opacity=".75" />
        <path d={ridge(262, 80, 28, 11)} fill="#140d24" opacity=".92" />
        <path d={ridge(300, 55, 22, 23)} fill="#0f0a1a" />

        {/* Castle hill, forest, castle */}
        <path d={HILL} fill={GROUND} />
        <path d={TREES} fill={GROUND} />
        <g transform="translate(720 236) scale(1.18) translate(-720 -236)">
          {CASTLE.map(([type, ...g], i) => (type === 'rect'
            ? <rect key={i} x={g[0]} y={g[1]} width={g[2]} height={g[3]} fill="#07070c" />
            : <polygon key={i} points={g[0]} fill="#07070c" />))}
          <g filter="url(#tvGlow)" fill="none" stroke="url(#tvHolo)" strokeWidth="1.2" strokeLinejoin="round">
            {CASTLE.map(([type, ...g], i) => (type === 'rect'
              ? <rect key={i} x={g[0]} y={g[1]} width={g[2]} height={g[3]} />
              : <polygon key={i} points={g[0]} />))}
            <line x1="721" y1="86" x2="721" y2="66" />
            <path d="M721 66 L736 71 L721 76" fill="#ff79c6" fillOpacity=".7" />
          </g>
          <path d="M714 236 L714 218 Q721 210 728 218 L728 236 Z" fill="#1a0f0a" stroke="#ffb86c" strokeOpacity=".5" />
          <g filter="url(#tvWindowGlow)">
            {WINDOWS.map(([x, y, w, h], i) => (
              <rect key={i} className="tv-window" x={x} y={y} width={w} height={h} rx={w / 2}
                fill={i % 3 ? '#ffb86c' : '#f1fa8c'} style={{ animationDelay: `${(i * 0.37) % 2.6}s` }} />
            ))}
          </g>
        </g>

        {/* Fade into the footer */}
        <rect x="0" y={H - 60} width={W} height="60" fill="url(#tvFade)" />
      </svg>

      <div className="tv-fog a" />
      <div className="tv-fog b" />

      {BATS.map((b, i) => (
        <div key={i} className="tv-bat" style={{ top: b.top, width: b.size, height: b.size * 0.47, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }}>
          <svg viewBox="0 0 64 30" fill="#1b1026">
            <path d="M32 12 C28 6 22 4 14 6 C18 9 18 13 14 16 C10 13 5 13 0 16 C8 18 13 24 16 28 C20 23 26 20 32 22 C38 20 44 23 48 28 C51 24 56 18 64 16 C59 13 54 13 50 16 C46 13 46 9 50 6 C42 4 36 6 32 12 Z" />
          </svg>
        </div>
      ))}
    </div>
  );
};

export default TransylvaniaScene;
